import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Trash2, Upload, Plus, ArrowLeft, GripVertical, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, absUploadUrl } from "../lib/apiClient";
import AdminShell from "./AdminShell";

const QUESTION_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "single_select", label: "Single choice" },
  { value: "multi_select", label: "Multi-choice" },
  { value: "file", label: "File upload" },
];

const EMPTY_LOCALE = {
  title: "",
  subtitle: "",
  description: "",
  terms: "",
  saving_badge: "",
  discount_label: "",
  questions: [],
};

const EMPTY_OFFER = {
  slug: "",
  title: "",
  subtitle: "",
  description: "",
  terms: "",
  hero_image_url: "",
  banner_image_url: "",
  list_image_url: "",
  popup_image_url: "",
  valid_until: "",
  price: 0,
  original_price: "",
  saving_badge: "",
  discount_label: "",
  currency: "CHF",
  active: true,
  featured: false,
  is_popup: false,
  position: 0,
  questions: [],
  translations: { fr: { ...EMPTY_LOCALE }, de: { ...EMPTY_LOCALE } },
  slug_fr: "",
  slug_de: "",
};

const TRANSLATABLE_FIELDS = ["title", "subtitle", "description", "terms", "saving_badge", "discount_label"];

function newQuestionId() {
  return "q_" + Math.random().toString(36).slice(2, 10);
}

export default function AdminOfferEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY_OFFER);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en"); // which language the translatable fields are editing

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/offers/${id}`);
        setForm({
          ...EMPTY_OFFER,
          ...data,
          valid_until: data.valid_until ? data.valid_until.slice(0, 10) : "",
          questions: (data.questions || []).map((q) => ({ ...q, id: q.id || newQuestionId() })),
          translations: {
            fr: { ...EMPTY_LOCALE, ...((data.translations && data.translations.fr) || {}) },
            de: { ...EMPTY_LOCALE, ...((data.translations && data.translations.de) || {}) },
          },
        });
      } catch (e) {
        toast.error(formatApiError(e, "Could not load offer"));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // Read/write the correct field based on active language
  function getField(k) {
    if (lang === "en") return form[k] || "";
    return (form.translations?.[lang]?.[k]) || "";
  }
  function setField(k, v) {
    if (lang === "en") {
      set(k, v);
    } else {
      setForm((f) => ({
        ...f,
        translations: {
          ...(f.translations || {}),
          [lang]: { ...(f.translations?.[lang] || {}), [k]: v },
        },
      }));
    }
  }

  function getQuestionField(q, field) {
    if (lang === "en") return q[field] ?? (field === "options" ? [] : "");
    const tr = (form.translations?.[lang]?.questions || []).find((x) => x.id === q.id);
    if (field === "options") return tr?.options ?? [];
    return tr?.[field] ?? "";
  }

  function setQuestionField(qId, field, value) {
    if (lang === "en") {
      setForm((f) => ({
        ...f,
        questions: f.questions.map((q) => (q.id === qId ? { ...q, [field]: value } : q)),
      }));
      return;
    }
    setForm((f) => {
      const trQs = [...(f.translations?.[lang]?.questions || [])];
      let entry = trQs.find((x) => x.id === qId);
      if (!entry) {
        entry = { id: qId };
        trQs.push(entry);
      }
      entry[field] = value;
      return {
        ...f,
        translations: {
          ...f.translations,
          [lang]: { ...(f.translations?.[lang] || {}), questions: trQs },
        },
      };
    });
  }

  function buildLocaleTranslations(localeCode) {
    const raw = form.translations?.[localeCode] || {};
    const out = Object.fromEntries(
      TRANSLATABLE_FIELDS.map((k) => [k, (raw[k] || "").trim()])
    );
    const trQs = (raw.questions || [])
      .map((q) => ({
        id: q.id,
        label: (q.label || "").trim(),
        help_text: (q.help_text || "").trim() || null,
        options: (q.options || []).filter(Boolean),
      }))
      .filter((q) => q.id && (q.label || q.help_text || q.options.length));
    if (trQs.length) out.questions = trQs;
    return out;
  }

  async function uploadImage(kind, file) {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await api.post("/admin/offers/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Inline uploads should return the URL to the caller instead of mutating form state
      if (kind === "inline") {
        toast.success("Image uploaded — inserted into description");
        return data.url;
      }
      set(`${kind}_image_url`, data.url);
      toast.success(`${kind} image uploaded`);
      return data.url;
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed"));
      return null;
    }
  }

  function addQuestion() {
    setForm((f) => ({
      ...f,
      questions: [
        ...f.questions,
        { id: newQuestionId(), label: "New question", type: "text", options: [], required: false, help_text: "" },
      ],
    }));
  }

  function updateQuestion(idx, patch) {
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    }));
  }

  function removeQuestion(idx) {
    setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  }

  function moveQuestion(idx, dir) {
    setForm((f) => {
      const arr = [...f.questions];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, questions: arr };
    });
  }

  async function save() {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const payload = {
      ...form,
      slug: (form.slug || form.title).trim(),
      slug_fr: (form.slug_fr || "").trim() || null,
      slug_de: (form.slug_de || "").trim() || null,
      price: Number(form.price) || 0,
      original_price: form.original_price === "" || form.original_price == null ? null : Number(form.original_price),
      position: Number(form.position) || 0,
      valid_until: form.valid_until ? new Date(form.valid_until + "T23:59:59Z").toISOString() : null,
      translations: {
        fr: buildLocaleTranslations("fr"),
        de: buildLocaleTranslations("de"),
      },
      questions: form.questions.map((q) => ({
        id: q.id || newQuestionId(),
        label: q.label,
        help_text: q.help_text || null,
        type: q.type,
        options: (q.options || []).filter(Boolean),
        required: !!q.required,
      })),
    };
    try {
      if (isNew) {
        const { data } = await api.post("/admin/offers", payload);
        toast.success("Offer created");
        nav(`/admin/offers/${data.id}`, { replace: true });
      } else {
        await api.patch(`/admin/offers/${id}`, payload);
        toast.success("Offer updated");
      }
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminShell>
        <div className="p-8 text-sm text-zinc-500">Loading...</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => nav("/admin/offers")}
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-oj-ink"
        >
          <ArrowLeft className="size-4" /> Back to offers
        </button>
        <button
          type="button"
          data-testid="admin-save-offer"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-oj-ink text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-zinc-800 disabled:opacity-60"
        >
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {isNew ? "Create offer" : "Save changes"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Basics">
            <LangSwitcher lang={lang} onChange={setLang} />
            <Field label={lang === "en" ? "Title (English)" : lang === "de" ? "Titel (Deutsch)" : "Titre (français)"}>
              <input
                data-testid="offer-title"
                value={getField("title")}
                onChange={(e) => setField("title", e.target.value)}
                className={inputCls}
                placeholder={lang === "en" ? "Get Tap-to-Pay free for 3 months" : lang === "de" ? "Tap-to-Pay 3 Monate gratis" : "Tap-to-Pay offert 3 mois"}
              />
              {lang !== "en" && !getField("title") && (
                <p className="mt-1 text-xs text-amber-600">Leave empty to fall back to English on the {lang.toUpperCase()} site.</p>
              )}
            </Field>
            <Field label="Slug (URL) — shared across languages">
              <input
                data-testid="offer-slug"
                value={form.slug}
                onChange={(e) => set("slug", e.target.value)}
                className={inputCls}
                placeholder="free-tap-to-pay"
              />
              <p className="mt-1 text-xs text-zinc-500">
                EN URL: /offers/<span className="font-mono">{form.slug || "auto-from-title"}</span>
              </p>
            </Field>
            {lang === "fr" && (
              <Field label="Slug français (URL FR) — auto if empty">
                <input
                  data-testid="offer-slug-fr"
                  value={form.slug_fr || ""}
                  onChange={(e) => set("slug_fr", e.target.value)}
                  className={inputCls}
                  placeholder="tap-to-pay-offert"
                />
                <p className="mt-1 text-xs text-zinc-500">
                  FR URL: /offers/fr/<span className="font-mono">{form.slug_fr || "auto-from-fr-title"}</span>
                </p>
              </Field>
            )}
            {lang === "de" && (
              <Field label="Slug Deutsch (URL DE) — auto if empty">
                <input
                  data-testid="offer-slug-de"
                  value={form.slug_de || ""}
                  onChange={(e) => set("slug_de", e.target.value)}
                  className={inputCls}
                  placeholder="all-in-one-angebot"
                />
                <p className="mt-1 text-xs text-zinc-500">
                  DE URL: /offers/de/<span className="font-mono">{form.slug_de || "auto-from-de-title"}</span>
                </p>
              </Field>
            )}
            <Field label={lang === "en" ? "Subtitle" : lang === "de" ? "Untertitel" : "Sous-titre"}>
              <input
                value={getField("subtitle")}
                onChange={(e) => setField("subtitle", e.target.value)}
                className={inputCls}
                placeholder={lang === "en" ? "Perfect for foodtrucks and pop-ups" : "Idéal pour foodtrucks et pop-ups"}
              />
            </Field>
            <Field label={lang === "en" ? "Description (Markdown)" : "Description (Markdown)"}>
              <MarkdownField
                value={getField("description")}
                onChange={(v) => setField("description", v)}
                onUpload={(f) => uploadImage("inline", f)}
              />
            </Field>
            <Field label={lang === "en" ? "Terms & conditions" : "Conditions générales"}>
              <textarea
                rows={4}
                value={getField("terms")}
                onChange={(e) => setField("terms", e.target.value)}
                className={inputCls}
              />
            </Field>
          </Card>

          <Card title="Signup form questions">
            <p className="text-xs text-zinc-500 -mt-2 mb-2">
              Ask visitors extra details when they claim this offer. Each answer is saved as a lead.
              {lang !== "en" && " Translate labels and options here — question type and required flag are edited in English."}
            </p>
            <div className="space-y-3">
              {form.questions.length === 0 && (
                <div className="text-sm text-zinc-500 italic">
                  No custom questions yet. Name / email / phone are always collected.
                </div>
              )}
              {form.questions.map((q, idx) => (
                <QuestionRow
                  key={q.id}
                  q={{
                    ...q,
                    label: getQuestionField(q, "label"),
                    help_text: getQuestionField(q, "help_text"),
                    options: getQuestionField(q, "options"),
                  }}
                  idx={idx}
                  count={form.questions.length}
                  translateOnly={lang !== "en"}
                  onChange={(patch) => {
                    Object.entries(patch).forEach(([field, value]) => setQuestionField(q.id, field, value));
                  }}
                  onRemove={() => removeQuestion(idx)}
                  onMove={(dir) => moveQuestion(idx, dir)}
                />
              ))}
            </div>
            {lang === "en" && (
              <button
                type="button"
                data-testid="add-question-btn"
                onClick={addQuestion}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-oj-ink px-3 py-2 rounded-md border border-dashed border-zinc-300 hover:border-oj-ink"
              >
                <Plus className="size-4" /> Add question
              </button>
            )}
          </Card>
        </div>

        {/* Side column */}
        <div className="space-y-6">
          <Card title="Visibility">
            <Toggle
              label="Active (shown publicly)"
              checked={form.active}
              onChange={(v) => set("active", v)}
              testId="toggle-active"
            />
            <Toggle
              label="Featured (top of list)"
              checked={form.featured}
              onChange={(v) => set("featured", v)}
              testId="toggle-featured"
            />
            <Toggle
              label="Show as first-visit popup"
              checked={form.is_popup}
              onChange={(v) => set("is_popup", v)}
              testId="toggle-popup"
            />
            <Field label="Position (lower = earlier)">
              <input
                type="number"
                value={form.position}
                onChange={(e) => set("position", e.target.value)}
                className={inputCls}
              />
            </Field>
          </Card>

          <Card title="Pricing & validity">
            <Field label="Currency">
              <input
                value={form.currency || "CHF"}
                onChange={(e) => set("currency", e.target.value.toUpperCase())}
                className={inputCls}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Original price">
                <input
                  type="number"
                  step="0.01"
                  value={form.original_price ?? ""}
                  onChange={(e) => set("original_price", e.target.value)}
                  className={inputCls}
                  placeholder="199"
                />
              </Field>
              <Field label="Sale price">
                <input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                  className={inputCls}
                  data-testid="offer-price"
                />
              </Field>
            </div>
            <p className="text-xs text-zinc-500 -mt-1">Set sale price to 0 for lead-only offers.</p>
            <Field label={lang === "en" ? "Saving badge (leave empty to auto-compute “Save X%”)" : "Badge d'économie (laisser vide pour auto)"}>
              <input
                value={getField("saving_badge")}
                onChange={(e) => setField("saving_badge", e.target.value)}
                className={inputCls}
                placeholder={lang === "en" ? "e.g. 3 months free" : "ex. 3 mois offerts"}
              />
            </Field>
            <Field label={lang === "en" ? "Card badge label (shown on offer card, header of hero)" : "Étiquette (affichée sur la carte et le header)"}>
              <input
                value={getField("discount_label")}
                onChange={(e) => setField("discount_label", e.target.value)}
                className={inputCls}
                placeholder={lang === "en" ? "Limited time" : "Durée limitée"}
              />
            </Field>
            <Field label="Valid until">
              <input
                type="date"
                value={form.valid_until || ""}
                onChange={(e) => set("valid_until", e.target.value)}
                className={inputCls}
              />
            </Field>
          </Card>

          <Card title="Images">
            <ImageUpload
              label="Banner image (shown at top of offer page, 1600×600)"
              url={form.banner_image_url}
              onUpload={(f) => uploadImage("banner", f)}
              onClear={() => set("banner_image_url", "")}
              testId="upload-banner"
            />
            <ImageUpload
              label="Hero background (1920×1080, used as backdrop)"
              url={form.hero_image_url}
              onUpload={(f) => uploadImage("hero", f)}
              onClear={() => set("hero_image_url", "")}
              testId="upload-hero"
            />
            <ImageUpload
              label="List card image (1200×630)"
              url={form.list_image_url}
              onUpload={(f) => uploadImage("list", f)}
              onClear={() => set("list_image_url", "")}
              testId="upload-list"
            />
            <ImageUpload
              label="Popup image (1200×900)"
              url={form.popup_image_url}
              onUpload={(f) => uploadImage("popup", f)}
              onClear={() => set("popup_image_url", "")}
              testId="upload-popup"
            />
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}

const inputCls =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-oj-brand";

function Card({ title, children }) {
  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5">
      <h3 className="text-sm uppercase tracking-wider font-semibold text-zinc-500 mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between cursor-pointer py-1.5">
      <span className="text-sm text-zinc-700">{label}</span>
      <input
        type="checkbox"
        data-testid={testId}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 accent-oj-brand"
      />
    </label>
  );
}

function ImageUpload({ label, url, onUpload, onClear, testId }) {
  const ref = useRef(null);
  return (
    <div data-testid={testId + "-wrapper"}>
      <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1.5">{label}</div>
      {url ? (
        <div className="relative rounded-md overflow-hidden border border-zinc-200">
          <img src={absUploadUrl(url)} alt="" className="w-full h-32 object-cover" />
          <button
            type="button"
            onClick={onClear}
            data-testid={testId + "-clear"}
            className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-600 rounded-md p-1.5 border border-zinc-200"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid={testId}
          onClick={() => ref.current?.click()}
          className="w-full h-24 rounded-md border-2 border-dashed border-zinc-300 hover:border-oj-brand flex items-center justify-center gap-2 text-sm text-zinc-500"
        >
          <Upload className="size-4" /> Upload image
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        onChange={(e) => onUpload(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
}

function QuestionRow({ q, idx, count, onChange, onRemove, onMove, translateOnly = false }) {
  const needsOptions = q.type === "single_select" || q.type === "multi_select";
  return (
    <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid={`question-row-${idx}`}>
      <div className="flex items-start gap-2">
        {!translateOnly && (
          <div className="flex flex-col gap-0.5 pt-1">
            <button type="button" onClick={() => onMove(-1)} disabled={idx === 0} className="text-zinc-400 hover:text-oj-ink disabled:opacity-30">
              <GripVertical className="size-4 rotate-90" />
            </button>
            <button type="button" onClick={() => onMove(1)} disabled={idx === count - 1} className="text-zinc-400 hover:text-oj-ink disabled:opacity-30">
              <GripVertical className="size-4 -rotate-90" />
            </button>
          </div>
        )}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-2">
          <input
            value={q.label || ""}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="Question label"
            className={`md:col-span-3 ${inputCls}`}
          />
          {translateOnly ? (
            <div className="md:col-span-3 text-xs text-zinc-500 self-center">
              Type: <span className="font-mono">{q.type}</span>{q.required ? " · required" : ""}
            </div>
          ) : (
            <>
          <select
            value={q.type}
            onChange={(e) => onChange({ type: e.target.value })}
            className={`md:col-span-2 ${inputCls}`}
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-zinc-600">
            <input
              type="checkbox"
              checked={!!q.required}
              onChange={(e) => onChange({ required: e.target.checked })}
              className="size-4 accent-oj-brand"
            />
            Required
          </label>
            </>
          )}
          <input
            value={q.help_text || ""}
            onChange={(e) => onChange({ help_text: e.target.value })}
            placeholder="Help text (optional)"
            className={`md:col-span-6 ${inputCls}`}
          />
          {needsOptions && (
            <textarea
              value={(q.options || []).join("\n")}
              onChange={(e) => onChange({ options: e.target.value.split(/\r?\n/).map((s) => s.trim()) })}
              rows={3}
              placeholder="One option per line"
              className={`md:col-span-6 ${inputCls}`}
            />
          )}
        </div>
        {!translateOnly && (
          <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700 pt-1">
            <Trash2 className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function MarkdownField({ value, onChange, onUpload }) {
  const ref = useRef(null);
  const fileRef = useRef(null);

  function insertAtCursor(text) {
    const el = ref.current;
    if (!el) {
      onChange((value || "") + text);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  }

  async function onPickImage(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const url = await onUpload(file);
    if (url) insertAtCursor(`\n\n![${file.name.replace(/\.[^.]+$/, "")}](${url})\n\n`);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs text-zinc-500">
          Supports **bold**, *italic*, `code`, lists, links, and images.
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="insert-image-btn"
          className="inline-flex items-center gap-1 text-xs font-semibold text-oj-brand hover:text-oj-ink px-2 py-1 rounded border border-zinc-300"
        >
          <ImageIcon className="size-3.5" /> Insert image
        </button>
      </div>
      <textarea
        ref={ref}
        rows={8}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls + " font-mono text-[13px]"}
        placeholder={"# Big headline\n\nAny paragraph here. **Bold** and _italic_ work.\n\n![Alt text](image-url) inserts inline images."}
        data-testid="description-textarea"
      />
      <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" />
    </div>
  );
}


function LangSwitcher({ lang, onChange }) {
  const opts = [
    { code: "en", label: "English" },
    { code: "de", label: "Deutsch" },
    { code: "fr", label: "Français" },
  ];
  return (
    <div className="mb-3 flex items-center justify-between gap-3 bg-zinc-100 rounded-lg p-1" data-testid="offer-lang-switcher">
      <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 ml-2">
        Editing:
      </div>
      <div className="inline-flex rounded-md overflow-hidden bg-white border border-zinc-200">
        {opts.map((o) => (
          <button
            key={o.code}
            type="button"
            onClick={() => onChange(o.code)}
            data-testid={`offer-lang-${o.code}`}
            className={`px-3 py-1.5 text-xs font-semibold transition ${
              lang === o.code ? "bg-oj-ink text-white" : "text-zinc-600 hover:text-oj-ink"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

